import { t } from 'i18next';
import { RouteObject } from 'react-router-dom';
import { BaseOutlet } from '../../../../components/BaseOutlet/BaseOutlet';
import {
  CurriculumVersionsListDocument,
  CurriculumVersionsListQuery,
  CurriculumVersionsListQueryVariables,
  SubjectByUuidDocument,
  SubjectByUuidQuery,
  SubjectByUuidQueryVariables,
} from '../../../../types/planung-graphql-client-defs';
import { urqlClient } from '../../../../utils/urqlClient';
import { CurriculumGradeSubpage } from './Curriculum/CurriculumGradeSubpage';
import { CurriculumGradesSubpage } from './Curriculum/CurriculumGradesSubpage';
import { CurriculumOverviewSubpage } from './Curriculum/CurriculumOverviewSubpage';
import { CurriculumPage } from './Curriculum/CurriculumPage';
import { CurriculumSubjectSubpage } from './Curriculum/CurriculumSubjectSubpage';
import { CurriculumSubjectsSubpage } from './Curriculum/CurriculumSubjectsSubpage';
import { CurriculumVersionsPage } from './CurriculumVersionsPage';

/** Lädt die Versionen und behält nur die der Route; die Brotkrumen lesen sie als `curriculumVersions[0]`. */
const loadCurriculumVersion = async (uuid: string) => {
  const result = await urqlClient
    .query<CurriculumVersionsListQuery, CurriculumVersionsListQueryVariables>(
      CurriculumVersionsListDocument,
      {},
      { additionalTypenames: ['CURRICULA', 'CURRICULUM_VERSIONS'] },
    )
    .toPromise();
  return {
    ...result,
    data: { curriculumVersions: result.data?.curriculumVersions.filter((version) => version.uuid === uuid) ?? [] },
  };
};

export const curriculumVersionRoutes = (baseSlug: string): RouteObject[] => {
  return [
    {
      element: <CurriculumVersionsPage />,
      index: true,
    },
    {
      path: `:curriculumUuid`,
      element: <CurriculumPage baseSlug={baseSlug} />,
      loader: ({ params }) => loadCurriculumVersion(params.curriculumUuid ?? ''),
      handle: {
        crumb: ({ data }: { data: CurriculumVersionsListQuery }) => ({
          slug: `${baseSlug}/${t('routes.curriculum.slug')}/${data.curriculumVersions[0].uuid}`,
          title: data.curriculumVersions[0].name,
        }),
      },
      children: [
        {
          element: <CurriculumOverviewSubpage />,
          index: true,
        },
        {
          element: <BaseOutlet />,
          path: t('routes.deputate.setup.grades.slug'),
          loader: ({ params }) => loadCurriculumVersion(params.curriculumUuid ?? ''),
          handle: {
            crumb: ({ data }: { data: CurriculumVersionsListQuery }) => ({
              slug: `${baseSlug}/${t('routes.curriculum.slug')}/${data.curriculumVersions[0].uuid}/${t('routes.deputate.setup.grades.slug')}`,
              title: t('routes.deputate.setup.grades.title'),
            }),
          },
          children: [
            {
              element: <CurriculumGradesSubpage />,
              index: true,
            },
            {
              path: `:grade`,
              element: <CurriculumGradeSubpage />,
              loader: ({ params }) => params,
              handle: {
                crumb: ({
                  curriculumUuid,
                  grade,
                }: {
                  curriculumUuid: string | undefined;
                  grade: string | undefined;
                }) => {
                  return {
                    slug: `${baseSlug}/${t('routes.curriculum.slug')}/${curriculumUuid}/${t('routes.deputate.setup.grades.slug')}/${grade}`,
                    title: grade ?? '',
                  };
                },
              },
            },
          ],
        },
        {
          element: <BaseOutlet />,
          path: t('routes.deputate.setup.subjects.slug'),
          loader: ({ params }) => loadCurriculumVersion(params.curriculumUuid ?? ''),
          handle: {
            crumb: ({ data }: { data: CurriculumVersionsListQuery }) => ({
              slug: `${baseSlug}/${t('routes.curriculum.slug')}/${data.curriculumVersions[0].uuid}/${t('routes.deputate.setup.subjects.slug')}`,
              title: t('routes.deputate.setup.subjects.title'),
            }),
          },
          children: [
            {
              element: <CurriculumSubjectsSubpage />,
              index: true,
            },
            {
              path: `:subjectUuid`,
              element: <CurriculumSubjectSubpage />,
              loader: async ({ params }) => {
                return urqlClient
                  .query<SubjectByUuidQuery, SubjectByUuidQueryVariables>(
                    SubjectByUuidDocument,
                    {
                      uuid: params.subjectUuid ?? '',
                    },
                    { additionalTypenames: ['SUBJECTS'] },
                  )
                  .toPromise();
              },
              handle: {
                crumb: ({ curriculumUuid, data }: { curriculumUuid: string | undefined; data: SubjectByUuidQuery }) => {
                  return {
                    slug: `${baseSlug}/${t('routes.curriculum.slug')}/${curriculumUuid}/${t('routes.deputate.setup.subjects.slug')}/${data.subject?.name}`,
                    title: data.subject?.name ?? '',
                  };
                },
              },
            },
          ],
        },
      ],
    },
  ];
};
